import React, {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Checkbox, SearchBox } from 'tea-component';
import { compareArraysEqual, uniqueArray } from '../../utils';
import {
  getAllKeys,
  optionsDealwith,
  optionsToMap,
  OptionsType,
  SelectOptionType,
} from '../../utils/customSelectUtils';
import DropdownBox from '../DropdownBox';
import './index.scss';
import './virtualScrollCustomSelect.scss';
import '@/pages/home/assets/scss/components/selectComponents.scss';
import { useIntl } from 'umi';
import { FilterKeys } from '../../types';
import { addFilterBuriedPoint } from '../../utils/point';
import _ from 'lodash-es';
import { useVirtualScroll } from '@op/hooks/useVirtualScroll';

type CustomSelectType = {
  searchInfo?: [string, (value: string) => void];
  selectOptionsInfo: [Array<any>, (arr: Array<any>) => void];
  subTextMap?: { [key: string]: string };
  basicOptions: OptionsType;
  options: OptionsType;
  onClear: () => void;
  placeholderText: string;
  buttonItemRender?: (value: any) => any;
  buttonAllRender?: any;
  optionItemRender?: (
    element: string | JSX.Element,
    value: string,
    item: any,
  ) => string | JSX.Element;
  allShowModal?: 'checkbox' | 'text';
  allLabel?: 'All' | string;
  textAllStyle?: 'big' | 'small';
  boxClassName?: string;
  showNumber?: number;
  pointKey?: FilterKeys;
  getBackData?: () => any;
  onChange: (value: any[]) => void;
  visibleCount: number;
  itemHeight: number;
  [key: string]: any;
};

/**
 * 自定义下拉组件，后期拓展可以考虑将checkBox最为参数传入，实现样式定制
 * @param options           选项
 * @param searchInfo        search关键字相关的信息
 * @param selectOptionsInfo 选中相关的信息
 * @param subTextMap        子标题文本的对象
 * @param onClear           清理按钮点击时触发
 * @param placeholderText   placeholder文案
 * @param buttonItemRender  input框展示渲染方法
 * @param optionItemRender  选项渲染方法，会传递文案作为参数
 * @param allShowModal      all的展示样式，有checkbox和text，
 * @param allLabel          all的展示文案
 * @param textAllStyle      allShowModal = text的时候，标题展示大小
 * @param showNumber        当选中数量小于或者等于showNumber时候，展示为具体内容，而不是数字
 *
 * @returns
 */
function VirtualScrollCustomSelect(
  {
    searchInfo,
    selectOptionsInfo,
    subTextMap,
    options,
    basicOptions,
    onClear,
    placeholderText,
    buttonItemRender,
    optionItemRender = (element) => element,
    allShowModal = 'checkbox',
    allLabel,
    textAllStyle = 'small',
    boxClassName,
    buttonAllRender,
    showNumber = 2,
    pointKey,
    getBackData,
    onChange,
    visibleCount = 8,
    itemHeight = 36,
    ...otherProps
  }: CustomSelectType,
  ref: React.Ref<unknown> | undefined,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = searchInfo || ['', () => {}];
  const [selectOptions, setSelectOptions] = selectOptionsInfo || [[], () => {}];
  const { formatMessage } = useIntl();
  /**
   * 原始数据获取所有的key，这个作为基准
   */
  const basicOptionsKeys = useMemo(
    () => getAllKeys(basicOptions),
    [basicOptions],
  );

  /**
   * 原始数据转为map，这个作为基准
   */
  const basicOptionsMap = useMemo(
    () => optionsToMap(basicOptions),
    [basicOptions],
  );

  /**
   * options的所有key，这个作为可以搜索的展示数据
   */
  const optionsKeys = useMemo(() => getAllKeys(options), [options]);

  // dropdown组件的button属性
  const buttonElement = (onlyResult?: boolean) => {
    const text = <span className="select-button-text">{placeholderText}</span>;
    const selectLen = selectOptions?.length || 0;
    let showElement: string | number | Array<JSX.Element> = selectLen;
    let isAll = false;
    // 没有数据，显示None
    if (basicOptionsKeys.length === selectLen) {
      showElement = buttonAllRender
        ? buttonAllRender
        : formatMessage({ id: 'All' });
      isAll = true;
    }
    // 选中的和选项一样多，就是ALL
    else if (selectLen === 0) {
      showElement = formatMessage({ id: 'None' });
    }
    // 如果是大于0 并小于3项就显示单项内容
    else if (selectLen <= showNumber) {
      // 如果是下拉开着的就使用tag 否则使用文本
      const len = selectOptions?.length - 1;
      showElement = selectOptions?.map((item: string, i: number) => {
        return (
          <Fragment key={item}>
            {buttonItemRender ? buttonItemRender(item) : basicOptionsMap[item]}
            {len !== i ? <span className="dbo-select-button-gap">, </span> : ''}
          </Fragment>
        );
      });
    }
    //  showElement === 'All' 这个参数很重要，一定不能返回错误
    return onlyResult ? (
      [showElement, isAll]
    ) : (
      <>
        {text} <span className="select-button-value">{showElement}</span>
      </>
    );
  };

  useImperativeHandle(ref, () => ({
    buttonElement: buttonElement(true),
  }));

  // 全选按钮的半选状态
  const [indeterminate, allSelect] = useMemo(() => {
    const len = selectOptions?.length;
    const allLen = basicOptionsKeys.length;
    return [allLen !== len && len > 0, allLen === len];
  }, [basicOptionsKeys, selectOptions]);

  // 全选复选框点击事件
  const toggleAllCheckbox = (flag: boolean) => {
    let selectArray: Array<string> = [];
    if (flag) {
      selectArray = uniqueArray([...(selectOptions || []), ...optionsKeys]);
    }
    setSelectOptions(selectArray);
  };

  /**
   * 针对不同的数据获取不同的元素
   */
  const groupElements = useMemo(() => {
    function renderArrayElement(item: SelectOptionType) {
      const { text, value } = item;
      return (
        <Checkbox className="dbo-select-checkbox-item" name={value} key={value}>
          {optionItemRender(text, value, item)}
        </Checkbox>
      );
    }

    function renderObjectElement(
      key: string,
      element: Array<SelectOptionType>,
    ) {
      const title = subTextMap?.[key];
      return (
        <Fragment key={key}>
          {element?.length > 0 && (
            <div
              style={{
                color: '#9b9fb0',
                margin: '7px 0 7px 10px',
                fontSize: 12,
              }}
            >
              {title}
            </div>
          )}
          {optionsDealwith(element, renderArrayElement)}
        </Fragment>
      );
    }

    const resultElements = optionsDealwith(
      options,
      renderArrayElement,
      renderObjectElement,
    );

    return resultElements;
  }, [options, formatMessage]);

  const allLabelElement = useMemo(() => {
    return allLabel || formatMessage({ id: 'All' });
  }, [allLabel, formatMessage]);

  const allElement = useMemo(() => {
    if (allShowModal === 'checkbox') {
      return (
        <Checkbox
          value={allSelect}
          className="select-all"
          indeterminate={indeterminate}
          onChange={toggleAllCheckbox}
        >
          {allLabelElement}
        </Checkbox>
      );
    }
    const length = Array.isArray(options)
      ? options.length
      : Object.values(options || {}).flat().length;

    const localAllSelect = allSelect || selectOptions?.length === length;

    return (
      <div className={`select-all-text ${textAllStyle}`}>
        <p className="title">
          {searchValue
            ? `${formatMessage({ id: 'Search Result' })} (${length})`
            : allLabelElement}
        </p>
        <Button type="link" onClick={() => toggleAllCheckbox(!localAllSelect)}>
          {formatMessage({ id: localAllSelect ? 'Clear All' : 'Select All' })}
        </Button>
      </div>
    );
  }, [allSelect, indeterminate, searchValue, options, formatMessage]);

  const beforeCacheValue = useRef<Array<any>>([]);

  // 使用自定义 Hook 实现虚拟滚动
  const {
    visibleItems,
    paddingTop,
    paddingBottom,
    containerProps,
    resetScroll,
  } = useVirtualScroll({
    itemHeight, // 每个选项的高度
    visibleCount, // 可见区域显示的选项数量
    totalCount: groupElements.length, // 总选项数量
  });

  useEffect(() => {
    if (isOpen) {
      resetScroll(); // 打开时重置滚动位置
    }
  }, [isOpen]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  return (
    <div className="dbo-custom-select-wrapper">
      <DropdownBox
        buttonElement={buttonElement}
        onClear={onClear}
        onOpen={() => {
          beforeCacheValue.current = [...selectOptions];
          setIsOpen(true);
        }}
        onClose={() => {
          setIsOpen(false);
          setSearchValue('');
          try {
            const [text, isAll] = buttonElement(true) as [any, boolean];
            let data = selectOptions;
            if (getBackData) {
              const item = getBackData();
              data = item.data;
            }
            addFilterBuriedPoint(pointKey, isAll ? 'All' : data);

            const flag = compareArraysEqual(
              beforeCacheValue.current,
              selectOptions,
            );
            !flag && onChange?.(selectOptions);
          } catch (e) {}
        }}
        className="dbo-custom-select-dropdown"
        boxClassName={`dbo-custom-select-box dbo-common-select-box ${boxClassName}`}
        {...otherProps}
      >
        {searchInfo && (
          <SearchBox
            autoFocus={true}
            value={searchValue}
            onChange={handleSearchChange}
          />
        )}
        <div className="dbo-common-select-body user-select">
          {allElement}
          <Checkbox.Group
            layout="column"
            className="dbo-virtual-scroll-group"
            value={selectOptions}
            onChange={(value: any) => {
              setSelectOptions(value);
            }}
          >
            {groupElements.length > 0 ? (
              <div {...containerProps}>
                <div
                  style={{
                    paddingTop: `${paddingTop}px`,
                    paddingBottom: `${paddingBottom}px`,
                  }}
                >
                  {visibleItems.map((index) => (
                    <div key={index} className="dbo-virtual-scroll-item">
                      {groupElements[index]}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="body-content user-select">
                <div className="tips">
                  {formatMessage({ id: 'No data yet' })}
                </div>
              </div>
            )}
          </Checkbox.Group>
        </div>
      </DropdownBox>
    </div>
  );
}

export default forwardRef(VirtualScrollCustomSelect);
